import { Component, OnInit, Inject, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Template, TemplateTask } from '../template';
import { TemplateService } from '../template.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { AlertService, AuthenticationService } from '@app/_services';
import { User } from '@app/_models';
import { first } from 'rxjs/operators';

@Component({
    selector: 'app-template',
    templateUrl: './template.component.html',
    styleUrls: ['./template.component.css'],
    providers: [MessageService],
    standalone: false
})
export class TemplateComponent implements OnInit {
  @Output() doAdd = new EventEmitter<Template>();
  @Output() doUpdate = new EventEmitter<Template>();

  form1: UntypedFormGroup;
  formLoading = false;
  template: Template;
  templateId: number;
  templateTasks: TemplateTask[] = [];
  currentUser: User;
  isEditMode = false;
  selectedTaskForTemplate: any = null;

  validationMessages = {
    name: [
      { type: 'required', message: 'Template name is required' }
    ]
  };

  constructor(
    private formBuilder: UntypedFormBuilder,
    private templateService: TemplateService,
    private alertService: AlertService,
    private messageService: MessageService,
    private authenticationService: AuthenticationService,
    public dialogRef: MatDialogRef<TemplateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.template = data?.template || null;
    this.templateId = this.template?.templateId;
    this.isEditMode = !!this.template;
    
    this.authenticationService.currentUser.subscribe(user => {
      this.currentUser = user;
    });
  }

  ngOnInit(): void {
    this.formBuild();
    if (this.template) {
      this.loadTemplateData();
    }
  }

  formBuild(): void {
    this.form1 = this.formBuilder.group({
      name: ['', Validators.required],
      description: [''],
      isActive: [true]
    });
  }

  loadTemplateData(): void {
    if (this.templateId) {
      this.formLoading = true;
      this.templateService.getById(this.templateId).pipe(first()).subscribe({
        next: (template) => {
          this.template = template;
          this.templateTasks = template.tasks || [];
          this.form1.patchValue({
            name: template.name,
            description: template.description,
            isActive: template.isActive
          });
          this.formLoading = false;
        },
        error: (err) => {
          this.alertService.error(err);
          this.formLoading = false;
        }
      });
    }
  }

  formSave(): void {
    if (this.form1.invalid) {
      this.form1.markAllAsTouched();
      return;
    }

    this.formLoading = true;
    const formValue = this.form1.value;
    formValue.createdBy = this.currentUser;

    if (this.templateId) {
      // Update
      formValue.templateId = this.templateId;
      formValue.dateCreated = this.template.dateCreated;
      this.templateService.update(formValue).pipe(first()).subscribe({
        next: (res) => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template updated successfully' });
          this.doUpdate.emit(res.data.template);
          this.dialogRef.close(res.data.template);
          this.formLoading = false;
        },
        error: (err) => {
          // Do NOT reset formLoading on error — a 504 means the update may have persisted.
          // Keep button disabled to block re-submit.
          this.alertService.error(err);
        }
      });
    } else {
      // Create
      this.templateService.add(formValue).pipe(first()).subscribe({
        next: (res) => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template created successfully' });
          this.templateId = res.data.template.templateId;
          this.template = res.data.template;
          this.doAdd.emit(res.data.template);
          // Don't close - allow adding tasks
          this.formLoading = false;
        },
        error: (err) => {
          // Do NOT reset formLoading on error — a 504 means the template may have been created.
          // Keep button disabled to prevent a duplicate template from being submitted.
          this.alertService.error(err);
        }
      });
    }
  }

  onTaskSelected(task: any): void {
    if (!task) return;
    
    // Commented out to show UI even when template not saved
    // if (!this.templateId) {
    //   this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please save the template first before adding tasks' });
    //   this.selectedTaskForTemplate = null;
    //   return;
    // }
    
    // Allow adding tasks to local array even if template not saved yet
    if (!this.templateId) {
      // Add to local array only, will be saved when template is saved
      const newTask: TemplateTask = {
        templateTaskId: 0,
        templateId: 0, // Will be set when template is saved
        taskId: task.taskId || 0,
        name: task.name || '',
        description: task.description || '',
        order: this.templateTasks.length + 1,
        isCompleted: false
      };
      this.templateTasks.push(newTask);
      this.messageService.add({ severity: 'info', summary: 'Info', detail: 'Task added locally. Save template to persist.' });
      this.selectedTaskForTemplate = null;
      return;
    }

    // Check if task already exists in template
    const taskExists = this.templateTasks.some(t => t.taskId === task.taskId);
    if (taskExists) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'This task is already in the template' });
      this.selectedTaskForTemplate = null;
      return;
    }

    const newTask: TemplateTask = {
      templateTaskId: 0,
      templateId: this.templateId,
      taskId: task.taskId || 0,
      name: task.name || '',
      description: task.description || '',
      order: this.templateTasks.length + 1,
      isCompleted: false
    };

    this.templateService.addTaskToTemplate(this.templateId, newTask).pipe(first()).subscribe({
      next: (res) => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Task added to template' });
        this.selectedTaskForTemplate = null; // Clear selection after adding
        this.loadTemplateData();
      },
      error: (err) => {
        this.alertService.error(err);
        this.selectedTaskForTemplate = null;
      }
    });
  }

  deleteTask(task: TemplateTask): void {
    if (confirm(`Delete task "${task.name}"?`)) {
      this.templateService.deleteTemplateTask(task.templateTaskId).pipe(first()).subscribe({
        next: (res) => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Task deleted' });
          this.loadTemplateData();
        },
        error: (err) => {
          this.alertService.error(err);
        }
      });
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  findInvalidControls(): string {
    const invalid = [];
    const controls = this.form1.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid.join(', ');
  }
}

